/**
 * Crop-rectangle geometry, in source-image pixels.
 *
 * Kept apart from the canvas work in `imageEdit` so it stays pure: no DOM, no
 * decoding, and every result is a rectangle that fits inside the image.
 */

import type { CropRect, ImageDimensions } from '@/lib/imageEdit';

/** Which grip is being dragged; `move` slides the whole rectangle. */
export type CropHandle = 'move' | 'nw' | 'ne' | 'sw' | 'se';

/** Small enough to crop tightly, large enough to stay grabbable. */
const MIN_EDGE = 16;

function bounded(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Rounds `rect` to whole pixels and keeps it inside `bounds`. */
export function clampRect(rect: CropRect, bounds: ImageDimensions): CropRect {
  // The ceiling is applied last: an image smaller than MIN_EDGE still yields a
  // rectangle that fits it, rather than one that overflows.
  const width = Math.min(Math.max(Math.round(rect.width), MIN_EDGE), bounds.width);
  const height = Math.min(Math.max(Math.round(rect.height), MIN_EDGE), bounds.height);
  return {
    width,
    height,
    x: bounded(Math.round(rect.x), 0, bounds.width - width),
    y: bounded(Math.round(rect.y), 0, bounds.height - height),
  };
}

/**
 * Forces `rect` to `aspect` (width ÷ height), shrinking rather than growing so
 * the result never spills outside the image.
 */
export function withAspect(
  rect: CropRect,
  aspect: number | null,
  bounds: ImageDimensions,
): CropRect {
  if (!aspect) return clampRect(rect, bounds);

  let width = rect.width;
  let height = width / aspect;
  if (height > bounds.height) {
    height = bounds.height;
    width = height * aspect;
  }
  if (width > bounds.width) {
    width = bounds.width;
    height = width / aspect;
  }
  return clampRect({ ...rect, width, height }, bounds);
}

/** The largest rectangle of `aspect` that fits, centred. */
export function centredRect(bounds: ImageDimensions, aspect: number | null): CropRect {
  const full = { x: 0, y: 0, width: bounds.width, height: bounds.height };
  if (!aspect) return full;
  const sized = withAspect(full, aspect, bounds);
  return clampRect(
    {
      ...sized,
      x: (bounds.width - sized.width) / 2,
      y: (bounds.height - sized.height) / 2,
    },
    bounds,
  );
}

/**
 * Where the rectangle lands after dragging `handle` by (`dx`, `dy`) pixels.
 *
 * Corner drags hold the opposite corner still, which is what makes the grip
 * feel attached to the pointer. Under a locked aspect the height follows the
 * width, so the anchored corner has to be recomputed afterwards.
 */
export function dragRect(
  start: CropRect,
  handle: CropHandle,
  dx: number,
  dy: number,
  bounds: ImageDimensions,
  aspect: number | null = null,
): CropRect {
  if (handle === 'move') {
    return clampRect({ ...start, x: start.x + dx, y: start.y + dy }, bounds);
  }

  const right = start.x + start.width;
  const bottom = start.y + start.height;
  let { x, y, width, height } = start;

  if (handle === 'nw') {
    x = start.x + dx;
    y = start.y + dy;
    width = right - x;
    height = bottom - y;
  } else if (handle === 'ne') {
    y = start.y + dy;
    width = start.width + dx;
    height = bottom - y;
  } else if (handle === 'sw') {
    x = start.x + dx;
    width = right - x;
    height = start.height + dy;
  } else {
    width = start.width + dx;
    height = start.height + dy;
  }

  if (aspect) {
    width = Math.max(width, MIN_EDGE);
    height = width / aspect;
    if (handle === 'nw' || handle === 'sw') x = right - width;
    if (handle === 'nw' || handle === 'ne') y = bottom - height;
  }

  return clampRect({ x, y, width, height }, bounds);
}

/**
 * How far `rect` is zoomed in: 1 is the largest frame of its shape the image
 * holds (`centredRect`), 2 is a frame half as wide, and so on.
 */
export function zoomOf(rect: CropRect, bounds: ImageDimensions, aspect: number | null): number {
  const widest = centredRect(bounds, aspect);
  return rect.width > 0 ? widest.width / rect.width : 1;
}

/**
 * `rect` at `zoom`, around its own middle - the slider twin of dragging a
 * corner, for anyone not dragging. Near an edge the frame slides back inside
 * rather than spilling, so the middle can drift a little there.
 */
export function zoomRect(
  rect: CropRect,
  zoom: number,
  bounds: ImageDimensions,
  aspect: number | null,
): CropRect {
  const widest = centredRect(bounds, aspect);
  const scale = Math.max(zoom, 1);
  const width = widest.width / scale;
  const height = widest.height / scale;
  const middleX = rect.x + rect.width / 2;
  const middleY = rect.y + rect.height / 2;
  return clampRect(
    { x: middleX - width / 2, y: middleY - height / 2, width, height },
    bounds,
  );
}

/**
 * The smallest frame of `aspect` that holds all of `content`, with `margin`
 * (a share of the content's longer edge) left clear on every side, centred on
 * it - what "trim empty space" crops to.
 *
 * Only ever smaller than the image: a crop cannot add canvas, so a tall mark
 * in a wide frame keeps what side room the file has and no more. When even
 * the whole image is too narrow for the shape, the largest frame that fits is
 * taken, centred on the content.
 */
export function aroundRect(
  content: CropRect,
  aspect: number | null,
  bounds: ImageDimensions,
  margin = 0,
): CropRect {
  const pad = Math.max(content.width, content.height) * margin;
  let width = content.width + pad * 2;
  let height = content.height + pad * 2;
  if (aspect) {
    if (width / height < aspect) width = height * aspect;
    else height = width / aspect;
  }
  const middleX = content.x + content.width / 2;
  const middleY = content.y + content.height / 2;
  const shaped = withAspect(
    { x: 0, y: 0, width: Math.min(width, bounds.width), height: Math.min(height, bounds.height) },
    aspect,
    bounds,
  );
  return clampRect(
    {
      ...shaped,
      x: middleX - shaped.width / 2,
      y: middleY - shaped.height / 2,
    },
    bounds,
  );
}
